// Format discount label
export function formatDiscount(coupon) {
  if (!coupon) return '';
  switch (coupon.discount_type) {
    case 'percent': return `${coupon.discount_value}% OFF`;
    case 'fixed':   return `$${coupon.discount_value} OFF`;
    case 'freebie': return 'FREE';
    case 'bogo':    return 'BOGO';
    case 'bundle':  return 'BUNDLE DEAL';
    default:        return 'DEAL';
  }
}

// Days until expiry
export function daysUntilExpiry(expiresAt) {
  if (!expiresAt) return null;
  const diff = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

export function expiryLabel(expiresAt) {
  if (!expiresAt) return 'No expiry';
  const days = daysUntilExpiry(expiresAt);
  if (days < 0)  return 'Expired';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  if (days <= 7)  return `Expires in ${days} days`;
  return `Expires ${new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

// Generate a session ID for anonymous tracking
/**
 * Make an outbound merchant link safe to hand to window.open.
 *
 * A duplicated scheme ("https://https://sephora.com") is read by the browser
 * as host "https" and dies with DNS_PROBE_FINISHED_NXDOMAIN, and a bare
 * "sephora.com" would resolve against our own origin. Both have shipped in
 * real data, so normalise here rather than trusting the stored value.
 * Returns null when there is nothing usable, so callers can stay put instead
 * of opening a broken tab.
 */
export function safeExternalUrl(raw) {
  let url = (raw || '').trim();
  if (!url) return null;

  // Collapse any number of repeated schemes, including the colon-less form
  // the address bar showed: https://https//x and https://https://x -> https://x
  url = url.replace(/^(?:(?:https?:?)?\/\/)+/i, (m) => (/^http:?\/\//i.test(m) ? 'http://' : 'https://'));

  // A scheme-less value ("sephora.com") would be treated as a relative path.
  if (!/^https?:\/\//i.test(url)) url = `https://${url.replace(/^\/+/, '')}`;

  try {
    const parsed = new URL(url);
    // Guard against the exact failure we saw: the host ended up as "https".
    if (!parsed.hostname || !parsed.hostname.includes('.')) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

export function getSessionId() {
  let sid = sessionStorage.getItem('pv_sid');
  if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('pv_sid', sid); }
  return sid;
}

// Truncate text
export function truncate(str, n = 80) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

// US States list
export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington D.C.' },
];

// Affiliate disclosure text (FTC requirement)
export const FTC_DISCLOSURE = 'We may earn a commission when you use our links — at no extra cost to you.';
